// components/features/agents/InviteAgentModal.tsx
import React, { useState } from "react";
import { Mail, UserPlus, Loader2 } from "lucide-react";

import useToken from "../../../hooks/features/auth/useToken";
import { alertSuccess, alertError } from "../../../helpers/alertError";
import { agentService } from "../../../services/agentService";
import BaseModal from "../../ui/Modal";
import { invitationService } from "../../../services/invitationService";

interface InviteAgentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export default function InviteAgentModal({ isOpen, onClose, onSuccess }: InviteAgentModalProps) {
  const { token } = useToken();
  const [email, setEmail] = useState("");
  const [departement, setDepartement] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email.trim()) {
      alertError("Veuillez saisir une adresse email");
      return;
    }

    setLoading(true);
    try {
      await invitationService.createInvitation(token!, {
        email: email.trim(),
        departement: departement.trim() || undefined,
      });
      alertSuccess(`Invitation envoyée à ${email}`);
      setEmail("");
      setDepartement("");
      onSuccess();
      onClose();
    } catch (error: any) {
      alertError(error.response?.data?.message || "Erreur lors de l'envoi de l'invitation");
    } finally {
      setLoading(false);
    }
  };

  return (
    <BaseModal isOpen={isOpen} onClose={onClose} title="Inviter un agent">
      <form onSubmit={handleSubmit} className="space-y-4 select-none">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Adresse email *
          </label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="agent@entreprise.com"
              className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:ring-4 focus:ring-primary/10 focus:border-primary focus:outline-none transition-all duration-300"
              required
            />
          </div>
          <p className="text-xs text-gray-400 mt-1">
            L'agent recevra un email avec un lien d'invitation
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Département (optionnel)
          </label>
          <input
            type="text"
            value={departement}
            onChange={(e) => setDepartement(e.target.value)}
            placeholder="Support Client"
            className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-4 focus:ring-primary/10 focus:border-primary focus:outline-none transition-all duration-300"
          />
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2.5 text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors font-medium"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={loading}
            className="flex items-center gap-2 px-5 py-2.5 bg-primary text-white rounded-xl hover:bg-primary/90 focus:outline-none focus:ring-4 focus:ring-primary/10 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 font-medium shadow-sm"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Envoi en cours...</span>
              </>
            ) : (
              <>
                <UserPlus className="w-4 h-4" />
                <span>Envoyer l'invitation</span>
              </>
            )}
          </button>
        </div>
      </form>
    </BaseModal>
  );
}